import axios, { AxiosRequestConfig } from "axios";
import { useState } from "react";
import { BaseResponse, HttpMethod } from "../models/response/base-response";
import { ApiConfig } from "../services/api-config";
import { useExpired } from "../state/expired-context";
import { useLoading } from "../state/loading-context";
import CookieUtils from "../utils/cookie-utils";

type FetchState = {
  loading: boolean;
  error: string | null;
};

const initialFetchState: FetchState = {
  loading: false,
  error: null,
};

interface UseFetchProps {
  endPointUrl?: string;
  showScreenLoading?: boolean;
  projectId?: string | undefined;
}

export const axiosInstance = axios.create({
  baseURL: ApiConfig.API_URL,
  headers: {
    "x-platform": "web",
    // BE đọc header `platform` (số) khi đăng nhập để gắn phiên + thiết bị theo enum DEVICE_TYPE
    // (WEB=1, MOBILE=2). Trước đây web không gửi → BE mặc định 0, lệch enum.
    platform: "1",
    "content-type": "application/json",
  },
  responseType: "json",
  timeout: 15000,
});

axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  (error: unknown) => {
    return Promise.reject(
      error instanceof Error ? error : new Error(String(error))
    );
  }
);

const useApi = <T>(
  method: HttpMethod,
  showLoading: boolean,
  projectId: string | undefined = ApiConfig.PROJECT_ID.USER_SERVICE + "",
  isToken: boolean = true
) => {
  const [fetchState, setFetchState] = useState<FetchState>(initialFetchState);
  const [baseResponse, setBaseResponse] = useState<BaseResponse<T>>();
  const { setIsLoading } = useLoading();
  const { setExpired } = useExpired();
  const finalToken = CookieUtils.getCurrentUser().access_token ?? "";

  // Do not register interceptors inside a hook to avoid creating
  // multiple handlers and calling React state setters outside render.
  // 401 is handled below in the request() flow.
  const request = async ({
    endPoint = "",
    data = {},
    isToken = true,
  }): Promise<BaseResponse<T> | undefined> => {
    if (showLoading) setIsLoading(true);
    setFetchState({ loading: true, error: null });
    try {
      const config: AxiosRequestConfig = {
        url: `/api/${endPoint}`,
        method,
        ...(method === HttpMethod.GET && { params: data }),
        ...(method === HttpMethod.POST && { data }),
        headers: {
          ...(projectId && { "x-svc-id": projectId, ProjectId: projectId }),
          ...(finalToken &&
            isToken && { Authorization: `Bearer ${finalToken}` }),
        },
      };
      const response = await axiosInstance(config);
      setFetchState({ loading: false, error: null });

      if ((response.status === 401 || response.data.status === 401) && finalToken) {
        setExpired(true);
      } else {
        setBaseResponse(response.data);
      }
      return response.data as BaseResponse<T>; // ✅ Return only `data`, with correct type
    } catch (error: unknown) {
      let errorMessage = "";
      if (axios.isCancel(error)) {
        console.error(
          "Request cancelled",
          error instanceof Error ? error.message : String(error)
        );
        errorMessage =
          error instanceof Error ? error.message : "Request cancelled";
      } else {
        errorMessage =
          error instanceof Error ? error.message : "An unknown error occurred";
      }
      setFetchState({ loading: false, error: errorMessage });

      // Axios NÉM lỗi với mọi HTTP 4xx/5xx, nên trước đây body phản hồi bị vứt và call site chỉ nhận
      // `undefined` → mọi lỗi đều phải hiện thông báo chung chung, mất hẳn `message` BE gửi kèm
      // (vd "Chi nhánh mới không có 2 dịch vụ của đơn"). Trả body ra để nơi gọi đọc `message`.
      // Nơi gọi vẫn an toàn: `status` trong body là 4xx nên `isSuccess`/`status === 200` vẫn false.
      if (axios.isAxiosError(error) && error.response?.data) {
        const body = error.response.data as BaseResponse<T>;
        const httpStatus = error.response.status;
        if ((httpStatus === 401 || Number((body as any)?.status) === 401) && finalToken) {
          // 401 thật cũng đi vào nhánh này (axios ném) — trước đây phiên hết hạn không được bắt.
          setExpired(true);
        } else {
          setBaseResponse(body);
        }
        return body;
      }
    } finally {
      if (showLoading) setIsLoading(false);
    }
  };

  return { baseResponse, request, ...fetchState };
};
// Wrapper for cleaner method access
export const useFetchData = {
  Get: <T>({
    projectId = ApiConfig.PROJECT_ID.USER_SERVICE,
    showScreenLoading = false,
  }: UseFetchProps = {}) => {
    return useApi<T>(HttpMethod.GET, showScreenLoading, projectId);
  },
  Post: <T>({
    projectId = ApiConfig.PROJECT_ID.USER_SERVICE,
    showScreenLoading = false,
  }: UseFetchProps = {}) => {
    return useApi<T>(HttpMethod.POST, showScreenLoading, projectId);
  },
};
